import { useEffect, useRef, useCallback } from "react";

export interface AutosaveOptions {
  /**
   * Delay in milliseconds before triggering autosave after changes
   * @default 2000
   */
  delay?: number;
  /**
   * Whether autosave is enabled
   * @default true
   */
  enabled?: boolean;
  /**
   * Callback when autosave is triggered
   */
  onSave: () => void | Promise<void>;
  /**
   * Callback when autosave succeeds
   */
  onSuccess?: () => void;
  /**
   * Callback when autosave fails
   */
  onError?: (error: Error) => void;
}

export interface AutosaveState {
  /**
   * Indicates if autosave is currently in progress
   */
  isSaving: boolean;
  /**
   * Timestamp of the last successful save
   */
  lastSavedAt: Date | null;
  /**
   * Manually trigger a save
   */
  triggerSave: () => void;
  /**
   * Cancel the save that is waiting, either on the debounce timer or deferred
   * behind an in-flight save. Leaves the in-flight save running. Returns
   * whether one was waiting, so a caller that runs the save itself knows
   * whether there was work to persist.
   */
  cancelPendingSave: () => boolean;
}

/**
 * Hook to provide autosave functionality with debouncing
 *
 * @example
 * const { isSaving, lastSavedAt, triggerSave } = useAutosave({
 *   delay: 2000,
 *   onSave: async () => {
 *     await saveRfpData(rfp);
 *   },
 *   onSuccess: () => console.log('Saved!'),
 *   onError: (err) => console.error('Save failed:', err)
 * });
 *
 * // Trigger autosave when data changes
 * useEffect(() => {
 *   triggerSave();
 * }, [rfpData]);
 */
export function useAutosave(options: AutosaveOptions): AutosaveState {
  const { delay = 2000, enabled = true, onSave, onSuccess, onError } = options;

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef(false);
  const pendingRef = useRef(false);
  const lastSavedAtRef = useRef<Date | null>(null);

  // Keep refs to the latest callbacks so the timeout/save closures
  // never go stale when the consumer re-renders with new props.
  const onSaveRef = useRef(onSave);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onSaveRef.current = onSave;
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  });

  const saveRef = useRef<(() => Promise<void>) | null>(null);

  const triggerSave = useCallback(() => {
    if (!enabled) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      saveRef.current?.();
    }, delay);
  }, [enabled, delay]);

  const cancelPendingSave = useCallback((): boolean => {
    const hadWaitingSave = !!timeoutRef.current || pendingRef.current;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    pendingRef.current = false;
    return hadWaitingSave;
  }, []);

  const save = useCallback(async () => {
    if (!enabled) return;

    if (isSavingRef.current) {
      // A save is already in-flight — mark pending so we re-trigger
      // after the current save completes instead of dropping the edit.
      pendingRef.current = true;
      return;
    }

    try {
      isSavingRef.current = true;
      await onSaveRef.current();
      lastSavedAtRef.current = new Date();
      onSuccessRef.current?.();
    } catch (error) {
      onErrorRef.current?.(
        error instanceof Error ? error : new Error(String(error)),
      );
    } finally {
      isSavingRef.current = false;
      if (pendingRef.current) {
        pendingRef.current = false;
        triggerSave();
      }
    }
  }, [enabled, triggerSave]);

  useEffect(() => {
    saveRef.current = save;
  });

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  return {
    isSaving: isSavingRef.current,
    lastSavedAt: lastSavedAtRef.current,
    triggerSave,
    cancelPendingSave,
  };
}
